// Columna de filtros del explorador de Ventas: cadena + los 8 filtros del mock. Es controlada: el
// estado vive en VentasPage. El monto va en pesos; `buildBackofficeSalesQuery` lo pasa a centavos.
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Input } from '@/kit';
import { FiltersColumn } from '@/shared/patterns/FiltersColumn';
import { ExternalProviderEnum, SaleTypeEnum } from '@/domains/sales/wizard/enums';
import type { BackofficeSalesFilters } from '@/api/services/retail-wizard/contract';
import { useRetailUsers, useStores } from '@/domains/retail/negocios/api/useRetailOrg';
import { useProducts } from '@/domains/retail/catalogo/api/useRetailCatalog';
import { itemDisplayName } from '@/shared/lib/format/itemDisplayName';
import { SearchInput } from '../../components';
import { EMPTY_FILTERS, activeFilterCount, dateShortcut } from '../lib/ventasFilters';
import {
    CENTRAL_CANCEL_STATUSES,
    CENTRAL_SYNC_STATUSES,
    centralStatusView,
} from '../lib/centralStatus';

/**
 * Valores centinela del selector de Financiera para las dos opciones que NO son proveedores externos.
 * Se eligen distintos de cualquier miembro de `ExternalProviderEnum` para que nunca se confundan con
 * uno, y nunca viajan al API tal cual: `financieraPatch` los traduce a un filtro por `type`.
 */
const FIN_CONTADO = '__CONTADO__';
const FIN_SUREKEEP = '__SUREKEEP__';

/** Qué opción se ve seleccionada, derivado del filtro REALMENTE aplicado (provider o type). */
function financieraValue(f: BackofficeSalesFilters): string {
    if (f.provider) return f.provider;
    if (f.type === SaleTypeEnum.CASH) return FIN_CONTADO;
    if (f.type === SaleTypeEnum.CREDIT) return FIN_SUREKEEP;
    return '';
}

/**
 * Traduce la opción elegida al filtro que el backend entiende. Cada rama limpia el otro campo: si no,
 * elegir PayJoy después de SureKeep dejaría `type: CREDIT` pegado y la lista saldría vacía —
 * ninguna venta es de tipo crédito Y de proveedor externo a la vez.
 */
function financieraPatch(v: string): Partial<BackofficeSalesFilters> {
    if (v === FIN_CONTADO) return { type: SaleTypeEnum.CASH, provider: undefined };
    if (v === FIN_SUREKEEP) return { type: SaleTypeEnum.CREDIT, provider: undefined };
    if (v) return { provider: v, type: SaleTypeEnum.EXTERNAL_REFERRAL };
    return { provider: undefined, type: undefined };
}

import { RetailerSelect } from './RetailerSelect';
import { ExportSalesButton } from './ExportSalesButton';
import { formatStorage } from '@/shared/lib/format/capacity';

/** Controles de 26px del mock; el `select` lleva su chevron propio en vez de la flecha del navegador. */
const CONTROL =
    'h-[26px] w-full appearance-none rounded-md border border-border bg-background pl-2 pr-[22px] text-[11.5px]';

/** Alto de los inputs de texto, número y fecha de la columna. */
const INPUT = 'h-[26px] px-2 text-[11.5px]';

/** Rótulo del grupo: 9.5px, negritas y espaciado ancho, como `.flt-label`. */
const GROUP_LABEL =
    'mb-1 block text-[9.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground';

interface FilterOption {
    value: string;
    label: string;
}

/** Rótulo del grupo (`.flt-label` del mock). */
function FieldLabel({ htmlFor, children }: { htmlFor: string; children: string }) {
    return (
        <label htmlFor={htmlFor} className={GROUP_LABEL}>
            {children}
        </label>
    );
}

/**
 * Select nativo con opción "todas" vacía.
 *
 * NO se usa `FilterBar`/`Select` del kit aquí aunque sean los componentes hermanos: son Radix, y un
 * `SelectItem` con `value=""` es un error en Radix — o sea, no hay forma de VOLVER a "todas" una vez
 * elegido un valor, que es justo lo que una barra de filtros necesita. Además `FilterBar` es
 * horizontal y usa el placeholder como rótulo, que desaparece al elegir: en una columna vertical de
 * 8 filtros el operador perdería de vista qué es cada control. El `<select>` nativo —el mismo que ya
 * usa `RetailerSelect` en esta columna— resuelve las dos cosas.
 */
function FilterSelect({
    label,
    value,
    onChange,
    options,
    allLabel,
}: {
    label: string;
    value: string;
    onChange: (value: string) => void;
    options: FilterOption[];
    allLabel: string;
}) {
    const id = useId();
    return (
        <div>
            <FieldLabel htmlFor={id}>{label}</FieldLabel>
            <div className="relative">
                <select
                    id={id}
                    className={CONTROL}
                    value={value}
                    onChange={(e) => {
                        onChange(e.target.value);
                    }}
                >
                    <option value="">{allLabel}</option>
                    {options.map((o) => (
                        <option key={o.value} value={o.value}>
                            {o.label}
                        </option>
                    ))}
                </select>
                <ChevronDown
                    className="pointer-events-none absolute right-1.5 top-1/2 size-3 -translate-y-1/2 text-muted-foreground"
                    aria-hidden
                />
            </div>
        </div>
    );
}

/** '' → `undefined`: un filtro vacío se OMITE del query, no se manda vacío. */
function orUndefined(value: string): string | undefined {
    return value === '' ? undefined : value;
}

/** El monto se teclea libre: vacío o basura ⇒ sin filtro, nunca `NaN` (que filtraría todo fuera). */
function parsePesos(raw: string): number | undefined {
    if (raw.trim() === '') return undefined;
    const n = Number(raw);
    return Number.isFinite(n) ? n : undefined;
}

export interface VentasFiltersProps {
    filters: BackofficeSalesFilters;
    onChange: (next: BackofficeSalesFilters) => void;
    /** Cuántas ventas hay traídas. Es el alcance REAL de la búsqueda, y la columna lo dice. */
    loadedCount?: number;
    /** `true` mientras queden páginas por traer: la búsqueda no cubre todavía todo el periodo. */
    hasMore?: boolean;
    collapsed: boolean;
    onToggleCollapsed: () => void;
    /** La cadena viene fija del rol: no hay nada que elegir y el grupo no se pinta. */
    retailerFixed?: boolean;
    /** Descarga lo listado; sin él no se ofrece exportar. */
    onExport?: () => void;
    canExport?: boolean;
}

export function VentasFilters({
    filters,
    onChange,
    loadedCount = 0,
    hasMore = false,
    collapsed,
    onToggleCollapsed,
    retailerFixed = false,
    onExport,
    canExport = false,
}: VentasFiltersProps) {
    const { t } = useTranslation('ventas');
    const montoMinId = useId();
    const montoMaxId = useId();
    const desdeId = useId();
    const hastaId = useId();

    // Las tres listas dependen de la cadena elegida y sus hooks ya traen `enabled: !!retailerId`,
    // así que sin cadena no se dispara ninguna request y los selects quedan solo con "todas".
    const { data: stores } = useStores(filters.retailerId);
    const { data: users } = useRetailUsers(filters.retailerId);
    const { data: products } = useProducts(filters.retailerId);

    /** Aplica un parche sobre los filtros vigentes — nunca reemplaza el objeto entero. */
    const set = (patch: Partial<BackofficeSalesFilters>): void => {
        onChange({ ...filters, ...patch });
    };

    /** Chip de tipo: volver a hacer clic en el activo lo APAGA (toggle del mock). */
    const toggleTipo = (tipo: SaleTypeEnum): void => {
        set({ type: filters.type === tipo ? undefined : tipo });
    };

    const activos = activeFilterCount(filters);

    const centralLabel = (status: string): string => {
        const view = centralStatusView(status);
        return t(`central.estado.${view.key}`, { defaultValue: view.defaultLabel });
    };

    const tipos: { key: SaleTypeEnum; label: string }[] = [
        { key: SaleTypeEnum.CASH, label: t('filtros.tipoContado', { defaultValue: 'Contado' }) },
        { key: SaleTypeEnum.CREDIT, label: t('filtros.tipoCredito', { defaultValue: 'Crédito' }) },
        {
            key: SaleTypeEnum.EXTERNAL_REFERRAL,
            label: t('filtros.tipoExterna', { defaultValue: 'Externa' }),
        },
    ];

    const atajos: { dias: number; label: string }[] = [
        { dias: 0, label: t('filtros.hoy', { defaultValue: 'Hoy' }) },
        { dias: 7, label: t('filtros.7d', { defaultValue: '7d' }) },
        { dias: 30, label: t('filtros.30d', { defaultValue: '30d' }) },
        { dias: -1, label: t('filtros.todo', { defaultValue: 'Todo' }) },
    ];

    return (
        <FiltersColumn
            collapsed={collapsed}
            onToggleCollapsed={onToggleCollapsed}
            title={t('filtros.titulo', { defaultValue: 'Filtros' })}
            activeCount={activos}
            resetDisabled={activos === 0}
            onReset={() => {
                // Limpiar conserva la cadena: sin ella la pantalla se queda sin contexto.
                onChange({ ...EMPTY_FILTERS, retailerId: filters.retailerId });
            }}
            bodyClassName="flex flex-col gap-2.5 pt-2.5"
        >
            {/* La cadena va PRIMERA y fuera del contador de filtros activos: sin ella la pantalla
                no tiene contexto (el query la usa como `index=retailer&value=…`). Cambiarla resetea
                tienda y vendedor, que son ids de OTRA cadena y no existirían en la nueva. */}
            {retailerFixed ? null : (
                <div>
                    <span className={GROUP_LABEL}>
                        {t('filtros.cadena', { defaultValue: 'Cadena' })}
                    </span>
                    <RetailerSelect
                        value={filters.retailerId}
                        onChange={(retailerId) => {
                            set({
                                retailerId,
                                storeId: undefined,
                                sellerId: undefined,
                                sku: undefined,
                            });
                        }}
                    />
                </div>
            )}

            {/* La búsqueda filtra en CLIENTE las ventas ya traídas (`filterByQuery`), no consulta al
                servidor: el `q` del endpoint es un filtro post-lectura que se salta lo que no cayó
                en la página. Por eso la columna declara sobre cuántas ventas está buscando. */}
            <div>
                <span className={GROUP_LABEL}>
                    {t('filtros.busqueda', { defaultValue: 'Búsqueda' })}
                </span>
                <SearchInput
                    className="[&_input]:h-[26px] [&_input]:text-[11.5px]"
                    value={filters.query ?? ''}
                    onChange={(v) => {
                        set({ query: orUndefined(v) });
                    }}
                    placeholder={t('filtros.buscarPlaceholder', {
                        defaultValue: 'Folio, cliente, CURP',
                    })}
                    ariaLabel={t('filtros.busqueda', { defaultValue: 'Búsqueda' })}
                />
                {hasMore ? (
                    <p className="mt-1 text-[10px] leading-snug text-muted-foreground">
                        {t('filtros.busquedaAlcanceParcial', {
                            n: loadedCount,
                            defaultValue:
                                'Busca en las {{n}} ventas cargadas. Trae más páginas para ampliar la búsqueda.',
                        })}
                    </p>
                ) : null}
            </div>

            {/* Financiera = proveedor EXTERNO. El crédito propio de SureKeep no viaja aquí: se
                persiste como `type: CREDIT` con `externalProvider: null`, así que se elige con el
                chip "Crédito" de abajo. Poner "SureKeep" en esta lista devolvería 0 resultados. */}
            <FilterSelect
                label={t('filtros.financiera', { defaultValue: 'Financiera' })}
                allLabel={t('filtros.todas', { defaultValue: 'Todas' })}
                value={financieraValue(filters)}
                onChange={(v) => {
                    set(financieraPatch(v));
                }}
                options={[
                    {
                        value: FIN_CONTADO,
                        label: t('financieras.contado', {
                            defaultValue: 'Contado (sin financiera)',
                        }),
                    },
                    {
                        value: FIN_SUREKEEP,
                        label: t('financieras.surekeep', { defaultValue: 'SureKeep' }),
                    },
                    ...Object.values(ExternalProviderEnum).map((p) => ({
                        value: p,
                        label: t(`financieras.${p}`, { defaultValue: p }),
                    })),
                ]}
            />

            <div>
                <span id="ventas-filtro-tipo" className={GROUP_LABEL}>
                    {t('filtros.tipo', { defaultValue: 'Tipo' })}
                </span>
                <div
                    role="group"
                    aria-labelledby="ventas-filtro-tipo"
                    className="grid grid-cols-3 gap-1"
                >
                    {tipos.map((tipo) => {
                        const activo = filters.type === tipo.key;
                        return (
                            <button
                                key={tipo.key}
                                type="button"
                                aria-pressed={activo}
                                onClick={() => {
                                    toggleTipo(tipo.key);
                                }}
                                className={cn(
                                    'h-[22px] rounded-full border px-1 text-[10px] font-medium transition-colors',
                                    activo
                                        ? 'border-primary bg-primary text-primary-foreground'
                                        : 'border-border text-foreground hover:bg-accent/50',
                                )}
                            >
                                {tipo.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            <FilterSelect
                label={t('filtros.producto', { defaultValue: 'Producto' })}
                allLabel={t('filtros.todos', { defaultValue: 'Todos' })}
                value={filters.sku ?? ''}
                onChange={(v) => {
                    set({ sku: orUndefined(v) });
                }}
                options={(products ?? []).map((p) => ({
                    value: p.sku,
                    label: [itemDisplayName(p), formatStorage(p.capacity)]
                        .filter(Boolean)
                        .join(' '),
                }))}
            />

            {/* Tienda y vendedor NO son filtros: son ÍNDICES del query (GSI). El builder del client
                traduce el que esté puesto a `index`+`value` con precedencia
                cliente > vendedor > tienda > cadena. Por eso elegir vendedor "gana" sobre tienda. */}
            <FilterSelect
                label={t('filtros.tienda', { defaultValue: 'Tienda' })}
                allLabel={t('filtros.todas', { defaultValue: 'Todas' })}
                value={filters.storeId ?? ''}
                onChange={(v) => {
                    set({ storeId: orUndefined(v) });
                }}
                options={(stores ?? []).map((s) => ({ value: s.storeId, label: s.name }))}
            />

            <FilterSelect
                label={t('filtros.vendedor', { defaultValue: 'Vendedor' })}
                allLabel={t('filtros.todos', { defaultValue: 'Todos' })}
                value={filters.sellerId ?? ''}
                onChange={(v) => {
                    set({ sellerId: orUndefined(v) });
                }}
                options={(users ?? []).map((u) => ({
                    value: u.retailUserId,
                    // `displayName` es opcional (DEC-SKF1-021). Sin él va un rótulo genérico —
                    // NUNCA el id crudo (DEC-012: no se renderizan UUIDs en la UI).
                    label:
                        u.displayName ??
                        t('filtros.usuarioSinNombre', { defaultValue: 'Sin nombre' }),
                }))}
            />

            <FilterSelect
                label={t('filtros.centralRegistro', { defaultValue: 'La Central · registro' })}
                allLabel={t('filtros.todos', { defaultValue: 'Todos' })}
                value={filters.centralSyncStatus ?? ''}
                onChange={(v) => {
                    set({ centralSyncStatus: CENTRAL_SYNC_STATUSES.find((st) => st === v) });
                }}
                options={CENTRAL_SYNC_STATUSES.map((st) => ({
                    value: st,
                    label: centralLabel(st),
                }))}
            />

            <FilterSelect
                label={t('filtros.centralCancelacion', {
                    defaultValue: 'La Central · cancelación',
                })}
                allLabel={t('filtros.todos', { defaultValue: 'Todos' })}
                value={filters.centralCancelStatus ?? ''}
                onChange={(v) => {
                    set({ centralCancelStatus: CENTRAL_CANCEL_STATUSES.find((st) => st === v) });
                }}
                options={CENTRAL_CANCEL_STATUSES.map((st) => ({
                    value: st,
                    label: centralLabel(st),
                }))}
            />

            <div>
                <span className={GROUP_LABEL}>
                    {t('filtros.monto', { defaultValue: 'Monto (MXN)' })}
                </span>
                <div className="grid grid-cols-2 gap-1">
                    <Input
                        id={montoMinId}
                        type="number"
                        inputMode="decimal"
                        min={0}
                        className={`${INPUT} tabular-nums`}
                        aria-label={t('filtros.montoMin', { defaultValue: 'Monto mínimo' })}
                        placeholder={t('filtros.min', { defaultValue: 'Mín' })}
                        value={filters.minPesos ?? ''}
                        onChange={(e) => {
                            set({ minPesos: parsePesos(e.target.value) });
                        }}
                    />
                    <Input
                        id={montoMaxId}
                        type="number"
                        inputMode="decimal"
                        min={0}
                        className={`${INPUT} tabular-nums`}
                        aria-label={t('filtros.montoMax', { defaultValue: 'Monto máximo' })}
                        placeholder={t('filtros.max', { defaultValue: 'Máx' })}
                        value={filters.maxPesos ?? ''}
                        onChange={(e) => {
                            set({ maxPesos: parsePesos(e.target.value) });
                        }}
                    />
                </div>
            </div>

            <div>
                <span className={GROUP_LABEL}>
                    {t('filtros.fecha', { defaultValue: 'Fecha de venta' })}
                </span>
                <div className="grid gap-1">
                    <Input
                        id={desdeId}
                        type="date"
                        className={INPUT}
                        aria-label={t('filtros.desde', { defaultValue: 'Desde' })}
                        value={filters.from ?? ''}
                        onChange={(e) => {
                            set({ from: orUndefined(e.target.value) });
                        }}
                    />
                    <Input
                        id={hastaId}
                        type="date"
                        className={INPUT}
                        aria-label={t('filtros.hasta', { defaultValue: 'Hasta' })}
                        value={filters.to ?? ''}
                        onChange={(e) => {
                            set({ to: orUndefined(e.target.value) });
                        }}
                    />
                </div>
                <div className="mt-1 flex flex-wrap gap-1">
                    {atajos.map((a) => (
                        <button
                            key={a.dias}
                            type="button"
                            onClick={() => {
                                // `dateShortcut` es la única fuente de verdad del rango (incluido
                                // `-1` = limpiar): la aritmética de fechas no se repite aquí.
                                set(dateShortcut(a.dias));
                            }}
                            className="h-6 rounded border border-border px-1.5 text-[10px] text-muted-foreground transition-colors hover:bg-accent/50"
                        >
                            {a.label}
                        </button>
                    ))}
                </div>
            </div>
            {onExport ? <ExportSalesButton canExport={canExport} onExport={onExport} /> : null}
        </FiltersColumn>
    );
}
